/**
 * LocalRoomReport — the pasted / hand-entered entry into the room report.
 *
 * 🔴 THIS FILE IS NOT THE PAGE ANY MORE (Sep 2 2026). RoomReport.jsx is, for
 * every source. Ben walked the pasted flow and found it had drifted into a
 * second, thinner product: no best buy, no worst overpay, no room-level steal
 * or fight, no market headline. The fix was not to copy those four things
 * across — it was to delete the parallel rendering. What lives here is the
 * local adapter's half of the contract: the copy only a typed or pasted room
 * can say, and its own two navigation buttons.
 *
 * 🔴 THE PAYOFF OF ENTERING A ROOM ONE TEAM AT A TIME (round 6, Aug 30 2026).
 * Ben: "push people to submit their entire league... let them slowly build their
 * league one team at a time." The reward for that work has to be the thing the
 * Sleeper league report gives a Sleeper room. It now is, literally: the same
 * component, the same cards, the same numbers where the data supports them.
 *
 * 🔴 IT ONLY EXISTS WHEN THE ROOM IS FULL, and the caller enforces that. A
 * partial room ranked against itself would rank the teams somebody happened to
 * type in and call it a league: the best of three entered teams is not the best
 * team in a ten-team auction, and a letter grade saying so would be a lie the
 * page told with a straight face. Until the count matches the league's own team
 * count, the strip above says how many are in and grades nothing.
 *
 * WHAT IS ROOM-RELATIVE HERE, and it is everything:
 *   the letter grade  — surplus rank inside this room, the league report's own
 *                       formula (roomGradesBySurplus, a documented mirror)
 *   the points rank   — week 1 projected points, highest first
 *   the position chips and every roster badge — terciles over these teams
 * The corpus percentiles a single team's report carries are deliberately absent:
 * one page, one yardstick. They are one tap away on any card.
 */

import { useMemo } from "react";
import { localRoomSource } from "../../utils/roomSource.js";
import RoomReport, { roomNavBtn } from "./RoomReport.jsx";

export default function LocalRoomReport({
  teams,
  settings,
  leagueName,
  onBack,
  onRateAnother,
  onOpenTeam,
  /* 🔴 "paste" | "manual", AND THE ROOM SHAPE CANNOT TELL THEM APART. Both are
     `source: "local"` to roomSource, which is right for rendering and useless
     for a funnel: pasting a whole draft and typing ten seats are the two paths
     whose conversion is the question. Only the caller knows which happened. */
  viewSource,
}) {
  const room = useMemo(
    () => localRoomSource(teams, settings, leagueName),
    [teams, settings, leagueName]
  );

  return (
    <RoomReport
      room={room}
      viewSource={viewSource}
      onOpenTeam={onOpenTeam}
      subline={
        <>
          All {settings.teams} teams entered.{" "}
          {room.allPriced
            ? "Every team is priced, so the letter is each team's surplus rank inside this room."
            : "Not every team is priced, so there is no market letter: the badge is each team's rank on projected points instead."}
        </>
      }
      actions={
        <>
          <button onClick={onBack} style={roomNavBtn}>
            Back to one team
          </button>
          {/* 🔴 THE WAY OUT THAT DOES NOT COST YOU THIS LEAGUE. Grading a room
              used to be a one-way door: the page held one league and starting
              a second meant losing the first. It is offered only because the
              room is saved by the time this renders (see TeamTab's save
              effect), so this genuinely puts the league down rather than
              deleting it — it is waiting in the picker on the landing page. */}
          {onRateAnother && (
            <button onClick={onRateAnother} data-testid="rate-another" style={roomNavBtn}>
              Rate another league
            </button>
          )}
        </>
      }
      footnote={
        <>
          This room is the teams you typed in, and every number on this page is measured inside it.
          Positional strength ranks each position's projected points among these {teams.length}{" "}
          teams: the sum of a team's best projected players at that position, counting one more than
          that position's own starting slots. The corpus percentiles on a single team's report are
          deliberately absent here, because one report should have one yardstick. "Rate this team"
          on any card opens that seat measured against the corpus instead.
          {room.allPriced &&
            " The letter is surplus rank inside this room, the same way the Sleeper league report" +
              " grades a fetched auction: auction money is close to zero-sum, so the room is the" +
              " curve and nobody gets an F."}
        </>
      }
    />
  );
}
